import { useState } from 'react';
import { cx } from '../../lib/cx';
import type { StaffNavGroup } from '../../lib/staff-nav';
import { BrandLogo } from '../ui/brand-logo';
import { ProfileMenu } from './profile-menu';
import { StaffNavList } from './staff-nav-list';

/** Side padding that centres a 44px rail item in the 72px collapsed rail. */
const RAIL_GUTTER = 'px-[calc((var(--mbm-layout-rail-width)-var(--mbm-size-rail-item-width))/2)]';

/**
 * The staff rail (Figma "Admin/Rail" collapsed, "Admin/Sidebar" open): 72px of icons that opens to 240px on hover or
 * keyboard focus. Open/closed is React state, so both layouts render through the same components. Vertical rhythm:
 * 20px top padding, a 40px logo row, then 24px group headers and 34px items, all 2px apart, in both states.
 * The logo row and the profile at the foot are fixed; the navigation between them scrolls when it is taller than the
 * window.
 */
export function StaffRail({ groups }: { groups: StaffNavGroup[] }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const collapsed = !(hovered || focused);

  return (
    <aside
      aria-label="Staff navigation"
      data-collapsed={collapsed}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      className={cx(
        'fixed inset-y-0 left-0 z-(--mbm-z-header) hidden flex-col border-r border-border-line bg-surface-card lg:flex',
        'pb-(--mbm-space-6) pt-(--mbm-space-5)',
        'transition-[width,box-shadow] duration-(--mbm-duration-base) ease-(--mbm-ease-standard)',
        collapsed ? 'w-(--mbm-layout-rail-width)' : 'w-(--mbm-layout-rail-expanded-width) shadow-(--mbm-shadow-lg)',
      )}
    >
      {/* The logo row stays put, like the client sidebar's: only the navigation below it scrolls. */}
      <div data-rail-brand className={cx('shrink-0', RAIL_GUTTER)}>
        <div className="flex h-(--mbm-space-8) items-center overflow-hidden pl-(--mbm-space-2-5)">
          <BrandLogo markOnly={collapsed} />
        </div>
      </div>
      <nav
        aria-label="Staff"
        className={cx(
          'mt-(--mbm-space-0-5) flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden',
          RAIL_GUTTER,
        )}
      >
        <StaffNavList groups={groups} collapsed={collapsed} />
      </nav>
      <div className="shrink-0 px-(--mbm-space-3) pt-(--mbm-space-2)">
        <ProfileMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}
